import { Component, OnInit } from '@angular/core';
import { ExternalAuthService } from '../../services/external-auth.service';
import { YourDriveService } from '../../services/yourdrive.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-yourdrive-integration',
  templateUrl: './yourdrive-integration.component.html',
  styleUrls: ['./yourdrive-integration.component.scss']
})
export class YourdriveIntegrationComponent implements OnInit {
  isLoading = false;
  currentToken: string | null = null;
  customToken: string | null = null;
  userData: any = null;
  isNewUser = false;
  tokenVerified = false;
  isLoggedInToYourDrive = false;
  yourDriveUserInfo: any = null;

  // YourDrive configuration
  yourDriveUrl = 'https://drive.accesslife.org'; // YourDrive frontend URL
  yourDriveApiUrl = 'https://driveapi.accesslife.org'; // YourDrive backend URL

  // Status messages
  statusMessage = '';
  errorMessage = '';

  constructor(
    private externalAuthService: ExternalAuthService,
    private yourDriveService: YourDriveService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadCurrentToken();
    this.checkYourDriveLoginStatus();
  }

  /**
   * Check if already logged into YourDrive
   */
  checkYourDriveLoginStatus(): void {
    this.isLoggedInToYourDrive = this.yourDriveService.isLoggedInToYourDrive();
    if (this.isLoggedInToYourDrive) {
      this.yourDriveUserInfo = this.yourDriveService.getUserInfo();
      console.log('Already logged into YourDrive:', this.yourDriveUserInfo);
    }
  }

  /**
   * Load current user's Firebase token
   */
  async loadCurrentToken(): Promise<void> {
    try {
      this.isLoading = true;
      this.currentToken = await this.externalAuthService.getCurrentUserToken();

      if (this.currentToken) {
        this.statusMessage = 'Firebase token loaded successfully';
        this.snackBar.open('Ready for YourDrive integration', 'OK', { duration: 3000 });
      } else {
        this.errorMessage = 'No user logged in';
        this.snackBar.open('Please log in first', 'ERROR', { duration: 3000 });
      }
    } catch (error: any) {
      this.errorMessage = `Failed to load token: ${error.message}`;
      this.snackBar.open('Failed to load token', 'ERROR', { duration: 3000 });
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Verify current Firebase token
   */
  verifyToken(): void {
    if (!this.currentToken) {
      this.snackBar.open('No token available', 'ERROR', { duration: 3000 });
      return;
    }

    this.isLoading = true;
    this.externalAuthService.verifyToken(this.currentToken).subscribe({
      next: (result) => {
        this.tokenVerified = true;
        this.statusMessage = `Token verified! UID: ${result.uid}, Email: ${result.email}`;
        this.snackBar.open('Token verified successfully', 'SUCCESS', { duration: 3000 });
        this.isLoading = false;
      },
      error: (error) => {
        this.tokenVerified = false;
        this.errorMessage = `Token verification failed: ${error.message}`;
        this.snackBar.open('Token verification failed', 'ERROR', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  /**
   * Login to YourDrive via .NET API
   */
  async loginToYourDrive(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';
    this.statusMessage = 'Connecting to YourDrive API...';

    try {
      const response = await this.yourDriveService.loginToYourDrive();

      this.statusMessage = `Successfully logged into YourDrive as ${response.userName}`;
      this.isLoggedInToYourDrive = true;
      this.yourDriveUserInfo = this.yourDriveService.getUserInfo();

      this.snackBar.open(
        `Welcome ${response.firstName}! You are now logged into YourDrive.`,
        'SUCCESS',
        { duration: 5000 }
      );

      console.log('YourDrive Login Response:', response);
    } catch (error: any) {
      this.errorMessage = error.message || 'Failed to login to YourDrive';
      this.snackBar.open(this.errorMessage, 'ERROR', { duration: 5000 });
      console.error('YourDrive login error:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Open YourDrive in new window with authentication
   */
  openYourDrive(): void {
    try {
      this.yourDriveService.openYourDrive();
      this.snackBar.open('Opening YourDrive...', 'INFO', { duration: 3000 });
    } catch (error: any) {
      this.errorMessage = error.message;
      this.snackBar.open(this.errorMessage, 'ERROR', { duration: 3000 });
    }
  }

  /**
   * Logout from YourDrive
   */
  logoutFromYourDrive(): void {
    this.yourDriveService.logout();
    this.isLoggedInToYourDrive = false;
    this.yourDriveUserInfo = null;
    this.statusMessage = 'Logged out from YourDrive';
    this.snackBar.open('Logged out from YourDrive', 'INFO', { duration: 3000 });
  }

  /**
   * Test backend connection (for debugging)
   */
  async testBackend(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';
    this.statusMessage = 'Testing backend connection...';

    try {
      const result = await this.yourDriveService.testBackendConnection();
      console.log('🧪 Test Result:', result);

      if (result.error) {
        this.errorMessage = `Backend test failed: ${result.error}`;
        this.snackBar.open('Backend test failed - check console', 'ERROR', { duration: 5000 });
      } else {
        this.statusMessage = 'Backend test successful - check console for details';
        this.snackBar.open('Backend test successful! Check console for response structure.', 'SUCCESS', { duration: 5000 });
      }
    } catch (error: any) {
      this.errorMessage = error.message || 'Backend test failed';
      this.snackBar.open(this.errorMessage, 'ERROR', { duration: 5000 });
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Copy token to clipboard
   */
  copyTokenToClipboard(token: string | null): void {
    if (!token) {
      this.snackBar.open('No token available', 'ERROR', { duration: 2000 });
      return;
    }

    navigator.clipboard.writeText(token).then(() => {
      this.snackBar.open('Token copied to clipboard', 'SUCCESS', { duration: 2000 });
    }).catch(() => {
      this.snackBar.open('Failed to copy token', 'ERROR', { duration: 2000 });
    });
  }

  /**
   * Refresh custom token
   */
  refreshToken(): void {
    this.isLoading = true;
    this.externalAuthService.refreshCustomToken().subscribe({
      next: (result) => {
        this.customToken = result.customToken;
        this.statusMessage = 'Token refreshed successfully';
        this.snackBar.open('Token refreshed', 'SUCCESS', { duration: 3000 });
        this.isLoading = false;
      },
      error: (error) => {
        this.errorMessage = `Token refresh failed: ${error.message}`;
        this.snackBar.open('Token refresh failed', 'ERROR', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  /**
   * Get embed authentication data
   */
  async getEmbedData(): Promise<void> {
    try {
      this.isLoading = true;
      const embedData = await this.externalAuthService.getEmbedAuthData();

      if (embedData) {
        console.log('Embed Auth Data:', embedData);
        this.statusMessage = 'Embed data generated. Check console for details.';
        this.snackBar.open('Embed data generated', 'SUCCESS', { duration: 3000 });
      } else {
        this.errorMessage = 'Failed to generate embed data';
        this.snackBar.open('Failed to generate embed data', 'ERROR', { duration: 3000 });
      }
    } catch (error: any) {
      this.errorMessage = `Embed data generation failed: ${error.message}`;
      this.snackBar.open('Failed to generate embed data', 'ERROR', { duration: 3000 });
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Clear all data and reset
   */
  clearData(): void {
    this.customToken = null;
    this.userData = null;
    this.isNewUser = false;
    this.tokenVerified = false;
    this.statusMessage = '';
    this.errorMessage = '';
    this.snackBar.open('Data cleared', 'INFO', { duration: 2000 });
  }
}

